import { apiFetch, apiUrl, parseApiResponse } from '../../config/apiBase';
import { donationFileImageSrc, truncateDonationText } from '../../utils/donationMedia';

/** 메인 페이지 매월 기부 현황 노출 건수 */
export const DONATION_MAIN_MONTHLY_SIZE = 4;

/** 메인 희망·스토리 노출 건수 */
export const DONATION_MAIN_HOPE_SIZE = 4;
export const DONATION_MAIN_STORY_SIZE = 4;

/** 목록 페이지 pageSize (4.0 UI) */
export const DONATION_HOPE_LIST_PAGE_SIZE = 10;
export const DONATION_STORY_LIST_PAGE_SIZE = 5;

/** 스토리 댓글 pageSize (3.0) */
export const DONATION_STORY_COMMENT_PAGE_SIZE = 5;

/** 월별 기부 내역 팝업 pageSize (3.0 popup pageSize: 6) */
export const DONATION_POPUP_PAGE_SIZE = 6;

/**
 * @param {number} donateYear
 * @param {string|number} donateMonth
 */
export function formatDonationMonthLabel(donateYear, donateMonth) {
  const y = String(donateYear ?? '').trim();
  const m = parseInt(String(donateMonth ?? '').trim(), 10);
  if (!y || Number.isNaN(m)) return '';
  return `${y}년 ${m}월`;
}

/**
 * 3.0 getDonationChildrenSum
 * @returns {Promise<{ donateCntSum: number, donateMoneySum: number }>}
 */
export async function fetchDonationSummary() {
  const res = await fetch(apiUrl('/api/info/donation/summary'));
  return parseApiResponse(res, '기부 누적 통계 조회 실패');
}

/**
 * 3.0 getDonationChildrenList
 * @param {number} page
 * @param {number} pageSize
 * @returns {Promise<{ items: Array<{ childrenNo: string, donateYear: string, donateMonth: string, donateCnt: number, donateMoney: number }>, totalCount: number }>}
 */
export async function fetchDonationMonthlyPage(page, pageSize) {
  const qs = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const res = await fetch(apiUrl(`/api/info/donation/monthly?${qs.toString()}`));
  return parseApiResponse(res, '월별 기부 현황 조회 실패');
}

/**
 * API 월별 목록 → 화면 카드/행 모델 (3.0: page=1 첫 행 = 진행중)
 * @param {Array} items
 * @param {number} page
 */
export function mapDonationMonthlyItems(items, page = 1) {
  const list = Array.isArray(items) ? items : [];
  return list.map((item, index) => {
    const date = formatDonationMonthLabel(item.donateYear, item.donateMonth);
    const isFirstOnPageOne = page === 1 && index === 0;
    if (isFirstOnPageOne) {
      return {
        id: item.childrenNo || `m-${index}`,
        date,
        current: true,
        statusMsg: '현재 진행중입니다.',
      };
    }
    return {
      id: item.childrenNo || `m-${index}`,
      date,
      current: false,
      participants: Number(item.donateCnt) || 0,
      amount: Number(item.donateMoney) || 0,
    };
  });
}

/**
 * @param {object} item
 */
export function mapDonationHopeItem(item) {
  const childrenNo = item?.childrenNo ?? '';
  const thumbsImgId = item?.thumbsImgId;
  return {
    childrenNo,
    title: item?.title ?? '',
    description: truncateDonationText(item?.cont, 140),
    cont: item?.cont ?? '',
    imageUrl: donationFileImageSrc(thumbsImgId),
    thumbsImgDesc: item?.thumbsImgDesc ?? '',
    hasThankLetter: item?.mailUseYn === 'Y',
    mailCont: item?.mailCont ?? '',
    mailImgId: item?.mailImgId ?? '',
  };
}

/**
 * @param {object} item
 */
export function mapDonationStoryItem(item) {
  const storyNo = item?.storyNo ?? '';
  return {
    storyNo,
    title: item?.title ?? '',
    description: item?.subTitle ?? '',
    imageUrl: donationFileImageSrc(item?.thumbsImgId),
    thumbsImgDesc: item?.thumbsImgDesc ?? '',
    creDt: item?.creDt,
  };
}

function buildListQuery(page, pageSize, searchCondition, searchKeyword, staDt, endDt) {
  const qs = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (searchCondition) qs.set('searchCondition', searchCondition);
  if (searchKeyword?.trim()) qs.set('searchKeyword', searchKeyword.trim());
  if (staDt) qs.set('staDt', staDt);
  if (endDt) qs.set('endDt', endDt);
  return qs;
}

export async function fetchDonationHopePage(page, pageSize, searchCondition, searchKeyword, staDt, endDt) {
  const qs = buildListQuery(page, pageSize, searchCondition, searchKeyword, staDt, endDt);
  const res = await fetch(apiUrl(`/api/info/donation/hope?${qs.toString()}`));
  return parseApiResponse(res, '희망을 전달하는 곳 목록 조회 실패');
}

export async function fetchDonationHopeDetail(childrenNo) {
  const res = await fetch(apiUrl(`/api/info/donation/hope/${encodeURIComponent(childrenNo)}`));
  if (res.status === 404) return null;
  return parseApiResponse(res, '희망을 전달하는 곳 상세 조회 실패');
}

export async function fetchDonationStoryPage(page, pageSize, searchCondition, searchKeyword, staDt, endDt) {
  const qs = buildListQuery(page, pageSize, searchCondition, searchKeyword, staDt, endDt);
  const res = await fetch(apiUrl(`/api/info/donation/story?${qs.toString()}`));
  return parseApiResponse(res, '행복나눔 100% 스토리 목록 조회 실패');
}

export async function fetchDonationStoryDetail(storyNo) {
  const res = await fetch(apiUrl(`/api/info/donation/story/${encodeURIComponent(storyNo)}`));
  if (res.status === 404) return null;
  return parseApiResponse(res, '행복나눔 100% 스토리 상세 조회 실패');
}

export async function fetchDonationStoryComments(storyNo, page, pageSize) {
  const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  const res = await fetch(apiUrl(`/api/info/donation/story/${encodeURIComponent(storyNo)}/comments?${qs.toString()}`));
  return parseApiResponse(res, '댓글 목록 조회 실패');
}

export async function postDonationStoryComment(storyNo, cont) {
  const res = await apiFetch(`/api/info/donation/story/${encodeURIComponent(storyNo)}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cont }),
  });
  await parseApiResponse(res, '댓글 등록에 실패하였습니다.');
}

export async function putDonationStoryComment(storyNo, cmntNo, cont) {
  const res = await apiFetch(
    `/api/info/donation/story/${encodeURIComponent(storyNo)}/comments/${encodeURIComponent(cmntNo)}/update`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cont }),
    }
  );
  await parseApiResponse(res, '댓글 수정에 실패하였습니다.');
}

export async function deleteDonationStoryComment(storyNo, cmntNo) {
  const res = await apiFetch(
    `/api/info/donation/story/${encodeURIComponent(storyNo)}/comments/${encodeURIComponent(cmntNo)}/remove`,
    { method: 'POST' }
  );
  await parseApiResponse(res, '댓글 삭제에 실패하였습니다.');
}
